import React from 'react';
import Head from 'next/head';

import {useRoute} from '../routes';
import {absoluteUrl, cleanPath, OG_IMAGE_HEIGHT, OG_IMAGE_WIDTH, ogImageUrl, SITE_NAME} from './site';

export type OgImage = {url: string; width?: number | null; height?: number | null; alt?: string | null; type?: string};

export type SeoProps = {
	/** содержимое <title> целиком */
	title: string;
	description: string;
	keywords?: string[];
	/** каноничный путь; по умолчанию — текущий адрес без query-строки */
	canonicalPath?: string;
	/** картинка для соцсетей: адрес сгенерированной карточки или картинка сущности с размерами; по умолчанию — карточка главной */
	ogImage?: string | OgImage;
	type?: 'website' | 'article';
	noindex?: boolean;
	jsonLd?: object | object[];
};

/**
 * Все мета-теги страницы. На странице рендерится ровно один <Seo>: у тегов есть key,
 * но порядок, в котором next/head сливает несколько экземпляров, на сервере и в браузере разный.
 */
export const Seo: React.FC<SeoProps> = ({
	title: rawTitle,
	description: rawDescription,
	keywords,
	canonicalPath,
	ogImage,
	type = 'website',
	noindex,
	jsonLd,
}) => {
	// в данных встречается «й» из двух символов (и + бреве) — приводим к одному
	const title = rawTitle.normalize('NFC');
	const description = rawDescription.normalize('NFC');
	const route = useRoute();
	const url = absoluteUrl(canonicalPath ?? cleanPath(route.asPath || '/'));
	const image: OgImage =
		typeof ogImage === 'object'
			? ogImage
			: {url: ogImage ?? ogImageUrl('section', 'index'), width: OG_IMAGE_WIDTH, height: OG_IMAGE_HEIGHT};

	const jsonLdItems = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

	return (
		<Head>
			<title key="title">{title}</title>
			<meta key="description" name="description" content={description} />
			{keywords && keywords.length > 0 && <meta key="keywords" name="keywords" content={keywords.join(', ').normalize('NFC')} />}
			<link key="canonical" rel="canonical" href={url} />
			<meta
				key="robots"
				name="robots"
				content={noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large'}
			/>

			<meta key="og:type" property="og:type" content={type} />
			<meta key="og:site_name" property="og:site_name" content={SITE_NAME} />
			<meta key="og:locale" property="og:locale" content="ru_RU" />
			<meta key="og:url" property="og:url" content={url} />
			<meta key="og:title" property="og:title" content={title} />
			<meta key="og:description" property="og:description" content={description} />
			<meta key="og:image" property="og:image" content={image.url} />
			<meta key="og:image:secure_url" property="og:image:secure_url" content={image.url} />
			{image.type && <meta key="og:image:type" property="og:image:type" content={image.type} />}
			{!!image.width && <meta key="og:image:width" property="og:image:width" content={String(image.width)} />}
			{!!image.height && <meta key="og:image:height" property="og:image:height" content={String(image.height)} />}
			<meta key="og:image:alt" property="og:image:alt" content={image.alt || title} />

			<meta key="twitter:card" name="twitter:card" content="summary_large_image" />
			<meta key="twitter:title" name="twitter:title" content={title} />
			<meta key="twitter:description" name="twitter:description" content={description} />
			<meta key="twitter:image" name="twitter:image" content={image.url} />
			<meta key="twitter:image:alt" name="twitter:image:alt" content={image.alt || title} />

			{jsonLdItems.map((item, index) => (
				<script
					key={`json-ld-${index}`}
					type="application/ld+json"
					dangerouslySetInnerHTML={{__html: JSON.stringify(item).replace(/</g, '\\u003c')}}
				/>
			))}
		</Head>
	);
};
